import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Typography,
  Tabs,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ControlOutlined,
} from "@ant-design/icons";
import { useAuth } from "shared/contexts/AuthProvider";
import { useNotification } from "shared/contexts/UserNotificationProvider";
import { useJoyControlMappingManagement } from "../hooks/useJoyControlMappingManagement";
import DEFAULT_JOY_CONTROL_MAPPING from "features/robot-teleop/constants/defaultJoyControlMapping";
import { BUTTON_STYLES } from "../constants";

const { Text } = Typography;
const { TextArea } = Input;

// ── helpers ──────────────────────────────────────────────────────────────────

const emptyMapping = () => ({
  name: "",
  description: "",
  axes_count: 6,
  buttons_count: 16,
  keyboard_bindings: [],
  gamepad_axis_bindings: [],
  gamepad_button_bindings: [],
});

// makeKey: deterministic key for antd table rows
let _rowCounter = 0;
const makeKey = () => String(++_rowCounter);

const withKeys = (arr) => arr.map((item) => ({ ...item, _key: makeKey() }));

// ── editable binding tables ───────────────────────────────────────────────────

const KeyboardBindingsTable = ({ bindings, onChange }) => {
  const addRow = () =>
    onChange([...bindings, { _key: makeKey(), key: "", type: "axis", index: 0, value: 1 }]);

  const removeRow = (_key) => onChange(bindings.filter((b) => b._key !== _key));

  const updateRow = (_key, field, value) =>
    onChange(bindings.map((b) => (b._key === _key ? { ...b, [field]: value } : b)));

  const columns = [
    {
      title: "Key Code",
      dataIndex: "key",
      render: (val, record) => (
        <Input
          size="small"
          value={val}
          placeholder="e.g. KeyW, Space"
          onChange={(e) => updateRow(record._key, "key", e.target.value)}
          style={{ width: 120 }}
        />
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (val, record) => (
        <Select
          size="small"
          value={val}
          onChange={(v) => updateRow(record._key, "type", v)}
          options={[
            { value: "axis", label: "Axis" },
            { value: "button", label: "Button" },
          ]}
          style={{ width: 90 }}
        />
      ),
    },
    {
      title: "Index",
      dataIndex: "index",
      render: (val, record) => (
        <InputNumber
          size="small"
          min={0}
          value={val}
          onChange={(v) => updateRow(record._key, "index", v ?? 0)}
          style={{ width: 70 }}
        />
      ),
    },
    {
      title: "Value",
      dataIndex: "value",
      render: (val, record) => (
        <InputNumber
          size="small"
          step={0.1}
          value={val}
          onChange={(v) => updateRow(record._key, "value", v ?? 0)}
          style={{ width: 80 }}
        />
      ),
    },
    {
      title: "",
      render: (_, record) => (
        <Button size="small" danger onClick={() => removeRow(record._key)}>
          ✕
        </Button>
      ),
      width: 50,
    },
  ];

  return (
    <>
      <Table
        size="small"
        dataSource={bindings}
        columns={columns}
        rowKey="_key"
        pagination={false}
        scroll={{ y: 240 }}
      />
      <Button size="small" icon={<PlusOutlined />} onClick={addRow} style={{ marginTop: 8 }}>
        Add row
      </Button>
    </>
  );
};

const GamepadAxesTable = ({ bindings, onChange }) => {
  const addRow = () =>
    onChange([
      ...bindings,
      { _key: makeKey(), gamepad_axis_index: 0, joy_axis_index: 0, scale: 1.0 },
    ]);

  const removeRow = (_key) => onChange(bindings.filter((b) => b._key !== _key));

  const updateRow = (_key, field, value) =>
    onChange(bindings.map((b) => (b._key === _key ? { ...b, [field]: value } : b)));

  const columns = [
    {
      title: "Gamepad Axis",
      dataIndex: "gamepad_axis_index",
      render: (val, record) => (
        <InputNumber size="small" min={0} value={val} onChange={(v) => updateRow(record._key, "gamepad_axis_index", v ?? 0)} style={{ width: 100 }} />
      ),
    },
    {
      title: "Joy Axis",
      dataIndex: "joy_axis_index",
      render: (val, record) => (
        <InputNumber size="small" min={0} value={val} onChange={(v) => updateRow(record._key, "joy_axis_index", v ?? 0)} style={{ width: 90 }} />
      ),
    },
    {
      title: "Scale",
      dataIndex: "scale",
      render: (val, record) => (
        <InputNumber size="small" step={0.1} value={val} onChange={(v) => updateRow(record._key, "scale", v ?? 1)} style={{ width: 80 }} />
      ),
    },
    {
      title: "",
      render: (_, record) => (
        <Button size="small" danger onClick={() => removeRow(record._key)}>✕</Button>
      ),
      width: 50,
    },
  ];

  return (
    <>
      <Table size="small" dataSource={bindings} columns={columns} rowKey="_key" pagination={false} scroll={{ y: 240 }} />
      <Button size="small" icon={<PlusOutlined />} onClick={addRow} style={{ marginTop: 8 }}>Add row</Button>
    </>
  );
};

const GamepadButtonsTable = ({ bindings, onChange }) => {
  const addRow = () =>
    onChange([
      ...bindings,
      { _key: makeKey(), gamepad_button_index: 0, joy_button_index: 0 },
    ]);

  const removeRow = (_key) => onChange(bindings.filter((b) => b._key !== _key));

  const updateRow = (_key, field, value) =>
    onChange(bindings.map((b) => (b._key === _key ? { ...b, [field]: value } : b)));

  const columns = [
    {
      title: "Gamepad Button",
      dataIndex: "gamepad_button_index",
      render: (val, record) => (
        <InputNumber size="small" min={0} value={val} onChange={(v) => updateRow(record._key, "gamepad_button_index", v ?? 0)} style={{ width: 120 }} />
      ),
    },
    {
      title: "Joy Button",
      dataIndex: "joy_button_index",
      render: (val, record) => (
        <InputNumber size="small" min={0} value={val} onChange={(v) => updateRow(record._key, "joy_button_index", v ?? 0)} style={{ width: 100 }} />
      ),
    },
    {
      title: "",
      render: (_, record) => (
        <Button size="small" danger onClick={() => removeRow(record._key)}>✕</Button>
      ),
      width: 50,
    },
  ];

  return (
    <>
      <Table size="small" dataSource={bindings} columns={columns} rowKey="_key" pagination={false} scroll={{ y: 240 }} />
      <Button size="small" icon={<PlusOutlined />} onClick={addRow} style={{ marginTop: 8 }}>Add row</Button>
    </>
  );
};

// ── MappingFormFields ─────────────────────────────────────────────────────────

const MappingFormFields = ({ formState, onChange }) => {
  const resetToDefaults = () => {
    onChange({
      ...formState,
      axes_count: DEFAULT_JOY_CONTROL_MAPPING.axes_count,
      buttons_count: DEFAULT_JOY_CONTROL_MAPPING.buttons_count,
      keyboard_bindings: withKeys(DEFAULT_JOY_CONTROL_MAPPING.keyboard_bindings),
      gamepad_axis_bindings: withKeys(DEFAULT_JOY_CONTROL_MAPPING.gamepad_axis_bindings),
      gamepad_button_bindings: withKeys(DEFAULT_JOY_CONTROL_MAPPING.gamepad_button_bindings),
    });
  };

  const tabItems = [
    {
      key: "keyboard",
      label: "Keyboard",
      children: (
        <KeyboardBindingsTable
          bindings={formState.keyboard_bindings}
          onChange={(v) => onChange({ ...formState, keyboard_bindings: v })}
        />
      ),
    },
    {
      key: "gamepad_axes",
      label: "Gamepad Axes",
      children: (
        <GamepadAxesTable
          bindings={formState.gamepad_axis_bindings}
          onChange={(v) => onChange({ ...formState, gamepad_axis_bindings: v })}
        />
      ),
    },
    {
      key: "gamepad_buttons",
      label: "Gamepad Buttons",
      children: (
        <GamepadButtonsTable
          bindings={formState.gamepad_button_bindings}
          onChange={(v) => onChange({ ...formState, gamepad_button_bindings: v })}
        />
      ),
    },
  ];

  return (
    <Form layout="vertical">
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Name" required validateStatus={formState.name ? "" : "error"} help={!formState.name ? "Required" : ""}>
            <Input
              value={formState.name}
              placeholder="e.g. Spot Default, GoCart Fast"
              onChange={(e) => onChange({ ...formState, name: e.target.value })}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Axes count">
            <InputNumber min={1} max={32} value={formState.axes_count} onChange={(v) => onChange({ ...formState, axes_count: v ?? 6 })} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Buttons count">
            <InputNumber min={1} max={64} value={formState.buttons_count} onChange={(v) => onChange({ ...formState, buttons_count: v ?? 16 })} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item label="Description">
        <TextArea rows={2} value={formState.description} onChange={(e) => onChange({ ...formState, description: e.target.value })} />
      </Form.Item>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <Tooltip title="Populate all binding tables with the built-in defaults">
          <Button size="small" icon={<ReloadOutlined />} onClick={resetToDefaults}>
            Reset to defaults
          </Button>
        </Tooltip>
      </div>
      <Tabs items={tabItems} size="small" />
    </Form>
  );
};

// ── strip _key before sending to API ────────────────────────────────────────

const stripKeys = (formState) => ({
  name: formState.name,
  description: formState.description,
  axes_count: formState.axes_count,
  buttons_count: formState.buttons_count,
  keyboard_bindings: formState.keyboard_bindings.map(({ _key, ...rest }) => rest),
  gamepad_axis_bindings: formState.gamepad_axis_bindings.map(({ _key, ...rest }) => rest),
  gamepad_button_bindings: formState.gamepad_button_bindings.map(({ _key, ...rest }) => rest),
});

// ── main page component ───────────────────────────────────────────────────────

const JoyControlMappingsManagement = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { notifyError } = useNotification();

  const {
    mappingsData,
    loadMappings,
    createMapping,
    updateMapping,
    deleteMapping,
    pagination,
  } = useJoyControlMappingManagement();

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const [addForm, setAddForm] = useState(emptyMapping());
  const [editForm, setEditForm] = useState(emptyMapping());

  useEffect(() => {
    if (!authLoading && isAuthenticated) loadMappings();
  }, [authLoading, isAuthenticated, loadMappings]);

  // ── Add ──────────────────────────────────────────────────────────────────
  const handleAddOk = async () => {
    if (!addForm.name.trim()) { notifyError("Name is required"); return; }
    try {
      await createMapping(stripKeys(addForm));
      setIsAddOpen(false);
      setAddForm(emptyMapping());
    } catch { /* handled in hook */ }
  };

  // ── Edit ─────────────────────────────────────────────────────────────────
  const openEdit = (record) => {
    setSelectedRecord(record);
    setEditForm({
      name: record.name,
      description: record.description || "",
      axes_count: record.axes_count ?? 6,
      buttons_count: record.buttons_count ?? 16,
      keyboard_bindings: withKeys(record.keyboard_bindings ?? []),
      gamepad_axis_bindings: withKeys(record.gamepad_axis_bindings ?? []),
      gamepad_button_bindings: withKeys(record.gamepad_button_bindings ?? []),
    });
    setIsEditOpen(true);
  };

  const handleEditOk = async () => {
    if (!editForm.name.trim()) { notifyError("Name is required"); return; }
    try {
      await updateMapping(selectedRecord.mapping_id, stripKeys(editForm));
      setIsEditOpen(false);
      setSelectedRecord(null);
    } catch { /* handled in hook */ }
  };

  // ── Delete ───────────────────────────────────────────────────────────────
  const handleDelete = (record) => {
    Modal.confirm({
      title: `Delete mapping "${record.name}"?`,
      content: "Robot types using this mapping must be unassigned first.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => deleteMapping(record.mapping_id),
    });
  };

  // ── table columns ─────────────────────────────────────────────────────────
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Description",
      dataIndex: "description",
      render: (text) => <Text>{text || "—"}</Text>,
    },
    {
      title: "Axes",
      dataIndex: "axes_count",
      width: 70,
      render: (v) => <Text>{v ?? 6}</Text>,
    },
    {
      title: "Buttons",
      dataIndex: "buttons_count",
      width: 80,
      render: (v) => <Text>{v ?? 16}</Text>,
    },
    {
      title: "KB bindings",
      dataIndex: "keyboard_bindings",
      width: 110,
      render: (arr) => <Text type="secondary">{arr?.length ?? 0} entries</Text>,
    },
    {
      title: "Mapping ID",
      dataIndex: "mapping_id",
      render: (text) => <Text type="secondary" copyable style={{ fontSize: 11 }}>{text}</Text>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      render: (_, record) => (
        <Space size="small">
          <Button type="primary" ghost icon={<EditOutlined />} onClick={() => openEdit(record)}>
            Edit
          </Button>
          <Button type="primary" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const modalProps = { width: 860, destroyOnClose: true };

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs={24} xl={24}>
          <Card
            variant="borderless"
            className="criclebox tablespace mb-24"
            title={<><ControlOutlined style={{ marginRight: 8 }} />Keyboard to JOY Control Mappings</>}
            extra={
              <Button
                icon={<PlusOutlined />}
                style={BUTTON_STYLES.add}
                onClick={() => { setAddForm(emptyMapping()); setIsAddOpen(true); }}
              >
                New Mapping
              </Button>
            }
          >
            <div className="table-responsive">
              <Table
                columns={columns}
                dataSource={mappingsData.results || []}
                rowKey="mapping_id"
                pagination={pagination}
                className="ant-border-space"
                onRow={(record) => ({ onDoubleClick: () => openEdit(record) })}
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Add Modal */}
      <Modal
        title="New Joy Control Mapping"
        open={isAddOpen}
        onOk={handleAddOk}
        onCancel={() => setIsAddOpen(false)}
        okText="Create"
        {...modalProps}
      >
        <MappingFormFields formState={addForm} onChange={setAddForm} />
      </Modal>

      {/* Edit Modal */}
      <Modal
        title={`Edit Mapping: ${selectedRecord?.name ?? ""}`}
        open={isEditOpen}
        onOk={handleEditOk}
        onCancel={() => { setIsEditOpen(false); setSelectedRecord(null); }}
        okText="Save"
        {...modalProps}
      >
        <MappingFormFields formState={editForm} onChange={setEditForm} />
      </Modal>
    </div>
  );
};

export default JoyControlMappingsManagement;
